/*
 * Weekend Bundle LP v2 interactivity.
 * Custom element <lp-weekend-bundle-v2> wraps the whole .lpwb landing page.
 * All queries are scoped to the element instance, so multiple instances never
 * collide and there is no global state. Two behaviours:
 *   1. wireCarousel(name): swipeable strips (steps / bundle / reviews) with
 *      dot + prev/next arrow controls, matched by data-carousel / data-dots-for
 *      / data-arrow-*-for attributes.
 *   2. wireStickyCta(): reveal the sticky add-to-cart bar once the hero scrolls
 *      out, and hide it again while the bottom zone (final offer / email /
 *      footer, which already carries a CTA) is in view. IntersectionObserver is
 *      used deliberately here, the sticky reveal is the requested behaviour.
 */
class LpWeekendBundleV2 extends HTMLElement {
  connectedCallback() {
    this.observers = [];
    try {
      ['steps', 'bundle', 'reviews'].forEach((name) => this.wireCarousel(name));
      this.wireStickyCta();
    } catch (error) {
      console.error('lp-weekend-bundle-v2: init failed', error);
    }
  }

  disconnectedCallback() {
    (this.observers || []).forEach((o) => o.disconnect());
    this.observers = [];
  }

  wireCarousel(name) {
    const track = this.querySelector(`[data-carousel="${name}"]`);
    const dotsWrap = this.querySelector(`[data-dots-for="${name}"]`);
    if (!track || !dotsWrap) return;

    const dots = Array.from(dotsWrap.querySelectorAll('button'));
    const cards = Array.from(track.children);
    if (!cards.length || !dots.length) return;

    const setActive = (i) => {
      dots.forEach((d, idx) => d.classList.toggle('is-active', idx === i));
    };
    const activeIndex = () => {
      const center = track.scrollLeft + track.clientWidth / 2;
      let best = 0;
      let bestDist = Infinity;
      cards.forEach((card, idx) => {
        const mid = card.offsetLeft + card.offsetWidth / 2;
        const dist = Math.abs(mid - center);
        if (dist < bestDist) { bestDist = dist; best = idx; }
      });
      return best;
    };
    const goTo = (i) => {
      const clamped = Math.max(0, Math.min(cards.length - 1, i));
      const card = cards[clamped];
      if (!card) return;
      track.scrollTo({
        left: card.offsetLeft - (track.clientWidth - card.offsetWidth) / 2,
        behavior: 'smooth',
      });
    };

    let raf = 0;
    track.addEventListener('scroll', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setActive(activeIndex()));
    }, { passive: true });

    dots.forEach((dot, idx) => dot.addEventListener('click', () => goTo(idx)));

    const prevBtn = this.querySelector(`[data-arrow-prev-for="${name}"]`);
    const nextBtn = this.querySelector(`[data-arrow-next-for="${name}"]`);
    prevBtn?.addEventListener('click', () => goTo(activeIndex() - 1));
    nextBtn?.addEventListener('click', () => goTo(activeIndex() + 1));

    setActive(activeIndex());
  }

  wireStickyCta() {
    const bar = this.querySelector('[data-sticky-cta]');
    const hero = this.querySelector('.hero');
    if (!bar || !hero || !('IntersectionObserver' in window)) return;

    let heroOut = false;
    let lowerIn = 0;
    const update = () => bar.classList.toggle('is-visible', heroOut && lowerIn === 0);

    const heroObs = new IntersectionObserver((entries) => {
      heroOut = !entries[0].isIntersecting;
      update();
    }, { threshold: 0 });
    heroObs.observe(hero);
    this.observers.push(heroObs);

    const lower = Array.from(this.querySelectorAll('.final-offer, .lp-email, .lp-footer'));
    if (lower.length) {
      const lowerObs = new IntersectionObserver((entries) => {
        entries.forEach((en) => { en.target.dataset.inView = en.isIntersecting ? '1' : '0'; });
        lowerIn = lower.filter((el) => el.dataset.inView === '1').length;
        update();
      }, { threshold: 0, rootMargin: '0px 0px -35% 0px' });
      lower.forEach((el) => lowerObs.observe(el));
      this.observers.push(lowerObs);
    }
  }
}

if (!customElements.get('lp-weekend-bundle-v2')) {
  customElements.define('lp-weekend-bundle-v2', LpWeekendBundleV2);
}
